import React, { useEffect } from 'react';
import { Modal, Button, Typography, Flex } from 'antd';
import { Crown, Check, Shield } from 'lucide-react';
import { trackUpgradeFunnel } from '../../services/analytics';

const { Title, Text } = Typography;

interface UpgradePromptProps {
  visible: boolean;
  onClose: () => void;
  feature?: string;
  description?: string;
  source?: 'navbar' | 'feature_gate' | 'settings';
  upgradeUrl?: string;
}

const PRO_FEATURES = [
  'Record in stunning 1080p & 2K',
  'AI script generation & coaching',
  'Extended recording (up to 2 hours)',
  'Priority export processing',
];

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  visible,
  onClose,
  feature,
  description,
  source = 'feature_gate',
  upgradeUrl = process.env.REACT_APP_BACKEND_URL || 'https://app.dadan.io'
}) => {
  useEffect(() => {
    if (visible) {
      trackUpgradeFunnel.modalView();
    }
  }, [visible]);

  const handleDismiss = () => {
    trackUpgradeFunnel.modalDismiss();
    onClose();
  };

  const buildUpgradeUrl = (path: string) => {
    const url = new URL(path, upgradeUrl);
    url.searchParams.set('ref', source);
    url.searchParams.set('utm_source', 'recorder');
    url.searchParams.set('utm_medium', 'app');
    url.searchParams.set('utm_campaign', 'upgrade_prompt');
    return url.toString();
  };

  const handleUpgrade = () => {
    trackUpgradeFunnel.modalCtaClick();
    const upgradeWindow = window.open(buildUpgradeUrl('/account/subscription'), '_blank');
    if (upgradeWindow) {
      trackUpgradeFunnel.checkoutRedirect(source);
    }
    onClose();
  };

  const handleLearnMore = () => {
    window.open(buildUpgradeUrl('/account/subscription'), '_blank');
  };

  const displayTitle = feature ? `Unlock ${feature}` : 'Unlock The Full Studio';
  const displayDescription = description || 'Get access to all Pro features and take your recordings to the next level.';

  return (
    <Modal
      open={visible}
      onCancel={handleDismiss}
      footer={null}
      centered
      width={480}
      zIndex={9999}
      className="upgrade-prompt-modal"
      closeIcon={null}
    >
      <Flex vertical align="center" gap={24} style={{ padding: '24px 0' }}>
        <Flex
          align="center"
          justify="center"
          style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 178, 41, 0.1)',
          }}
        >
          <Crown size={40} fill="#ffb229" color="#ffb229" />
        </Flex>

        <Title level={3} style={{ margin: 0, textAlign: 'center' }}>
          {displayTitle}
        </Title>

        <Text
          type="secondary"
          style={{
            fontSize: 16,
            textAlign: 'center',
            maxWidth: 360,
          }}
        >
          {displayDescription}
        </Text>

        <Flex
          vertical
          gap={12}
          style={{
            width: '100%',
            maxWidth: 320,
            padding: '16px 20px',
            background: 'rgba(74, 60, 250, 0.04)',
            borderRadius: 12,
          }}
        >
          {PRO_FEATURES.map((featureText, index) => (
            <Flex key={index} align="center" gap={12}>
              <Flex
                align="center"
                justify="center"
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  backgroundColor: '#4A3CFA',
                  flexShrink: 0,
                }}
              >
                <Check size={12} color="white" strokeWidth={3} />
              </Flex>
              <Text style={{ fontSize: 14, color: '#1a1a2e' }}>
                {featureText}
              </Text>
            </Flex>
          ))}
        </Flex>

        <Flex gap={12} style={{ width: '100%', justifyContent: 'center' }}>
          <Button size="large" onClick={handleDismiss}>
            Maybe Later
          </Button>
          <Button
            type="primary"
            size="large"
            icon={<Crown size={16} />}
            onClick={handleUpgrade}
          >
            See Plans & Pricing
          </Button>
        </Flex>

        <Flex align="center" gap={6} style={{ opacity: 0.7 }}>
          <Shield size={14} color="#6b7280" />
          <Text type="secondary" style={{ fontSize: 13 }}>
            30-day money-back guarantee
          </Text>
        </Flex>

        <Text
          type="secondary"
          style={{ 
            fontSize: 14, 
            cursor: 'pointer',
            textDecoration: 'underline',
            textUnderlineOffset: 2,
          }}
          onClick={handleLearnMore}
        >
          Learn more about Pro features
        </Text>
      </Flex>
    </Modal>
  );
};
